import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Music, Mic2, Radio, Video, Calendar, Clock, Loader2, ChevronLeft, Upload, Phone, User } from 'lucide-react';

const StudioMusicalPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, isAdminAdil } = useAuth();
  const [loading, setLoading] = useState(false);

  // État Formulaire 1 - Salle de Répétition
  const [salleForm, setSalleForm] = useState({
    nom_complet: '',
    whatsapp: '',
    jour: '',
    heure_debut: '',
    heure_fin: ''
  });
  const [montantSalle, setMontantSalle] = useState(0);
  const [heuresTotal, setHeuresTotal] = useState(0);

  // État Formulaire 2 - Mixage Audio
  const [mixageForm, setMixageForm] = useState({
    nom_complet: '',
    whatsapp: '',
    titre_chanson: '',
    fichier_audio: null,
    notes: ''
  });

  const jours = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  // Calcul automatique du montant pour la salle de répétition
  useEffect(() => {
    if (salleForm.heure_debut && salleForm.heure_fin) {
      const [heureD, minD] = salleForm.heure_debut.split(':').map(Number);
      const [heureF, minF] = salleForm.heure_fin.split(':').map(Number);
      
      const debut = heureD + (minD / 60);
      const fin = heureF + (minF / 60);
      
      if (fin > debut) {
        const heures = fin - debut;
        setHeuresTotal(heures);
        setMontantSalle(heures * 10);
      } else {
        setHeuresTotal(0);
        setMontantSalle(0);
      }
    }
  }, [salleForm.heure_debut, salleForm.heure_fin]);

  const validateHeure = (heure) => {
    if (!heure) return true;
    const [h] = heure.split(':').map(Number);
    return h >= 8 && h < 18;
  };

  const handleSalleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/auth');
      return;
    }

    // Validations
    if (!salleForm.nom_complet || !salleForm.whatsapp || !salleForm.jour || !salleForm.heure_debut || !salleForm.heure_fin) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!validateHeure(salleForm.heure_debut) || !validateHeure(salleForm.heure_fin)) {
      toast.error('Désolé, le studio est ouvert de 08h00 à 18h00 uniquement');
      return;
    }

    const [heureF] = salleForm.heure_fin.split(':').map(Number);
    if (heureF > 18) {
      toast.error('Désolé, le studio ferme à 18h00');
      return;
    }

    if (montantSalle <= 0) {
      toast.error('L\'heure de fin doit être après l\'heure de début');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('nom_complet', salleForm.nom_complet);
      formData.append('whatsapp', salleForm.whatsapp);
      formData.append('jour', salleForm.jour);
      formData.append('heure_debut', salleForm.heure_debut);
      formData.append('heure_fin', salleForm.heure_fin);
      formData.append('montant', montantSalle);
      formData.append('type_service', 'salle_repetition');

      const response = await api.post('/studios-musicaux/reserver-salle', formData);

      if (response.data.success) {
        toast.success('✅ Réservation initiée ! Procédez au paiement');
        
        const message = `Réservation: ${salleForm.jour} de ${salleForm.heure_debut} à ${salleForm.heure_fin} - ${montantSalle}$`;
        toast.info(message);
        
        setSalleForm({
          nom_complet: '',
          whatsapp: '',
          jour: '',
          heure_debut: '',
          heure_fin: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const handleMixageSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour mixer votre chanson');
      navigate('/auth');
      return;
    }

    if (!mixageForm.nom_complet || !mixageForm.whatsapp || !mixageForm.titre_chanson || !mixageForm.fichier_audio) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('nom_complet', mixageForm.nom_complet);
      formData.append('whatsapp', mixageForm.whatsapp);
      formData.append('titre_chanson', mixageForm.titre_chanson);
      formData.append('fichier_audio', mixageForm.fichier_audio);
      formData.append('notes', mixageForm.notes || '');
      formData.append('montant', 30);
      formData.append('type_service', 'mixage_audio');

      const response = await api.post('/studios-musicaux/reserver-mixage', formData);

      if (response.data.success) {
        toast.success('✅ Demande de mixage envoyée ! Procédez au paiement');
        
        setMixageForm({
          nom_complet: '',
          whatsapp: '',
          titre_chanson: '',
          fichier_audio: null,
          notes: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav)$/i)) {
        toast.error('Veuillez uploader un fichier audio MP3 ou WAV');
        return;
      }
      
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 50MB');
        return;
      }
      
      setMixageForm({ ...mixageForm, fichier_audio: file });
      toast.success(`Fichier "${file.name}" sélectionné`);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#E50914] transition-colors font-semibold"
          >
            <ChevronLeft size={20} />
            <span>Retour</span>
          </button>
          <h1 className="text-2xl font-bold text-[#E50914]" style={{ fontFamily: 'Outfit' }}>
            STUDIO MUSICAL
          </h1>
          {!isAuthenticated && (
            <Button
              onClick={() => navigate('/auth')}
              className="bg-[#E50914] hover:bg-[#F8212C] text-white"
            >
              Connexion
            </Button>
          )}
          {isAuthenticated && (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-gray-50 to-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-[#E50914] to-orange-600 mb-6 shadow-lg">
            <Music size={40} className="text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 text-gray-900" style={{ fontFamily: 'Outfit' }}>
            🎵 STUDIO MUSICAL
          </h1>
          <p className="text-gray-700 text-lg sm:text-xl max-w-3xl mx-auto font-medium">
            Réservez votre créneau ou faites mixer votre chanson par nos ingénieurs professionnels
          </p>
        </div>
      </div>

      {/* Galerie Photos */}
      <div className="max-w-7xl mx-auto px-4 py-12 bg-white">
        <h2 className="text-3xl font-bold text-center mb-3 text-gray-900">📸 Notre Studio</h2>
        <p className="text-gray-600 text-center mb-8">Découvrez nos installations professionnelles et notre inauguration officielle</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/qex9e87y_825311084_1634662974860523_9110117953571647023_n.jpg", label: "🎉 Inauguration officielle" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/r8bhyi98_825311004_2137289280474378_9021580020960634434_n.jpg", label: "👥 Notre équipe" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/rslaop48_825884563_1911025260317374_4175283867395551670_n.jpg", label: "🎚️ Console de mixage" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/sp7d2scc_825311040_1418379657098322_765873490392249633_n.jpg", label: "🎤 Cabine d'enregistrement" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/ii53hwvh_825309681_1802886190714274_8721990666132205740_n.jpg", label: "🎊 Grand lancement", span: 2 },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/twetfn60_825311956_4391652151087937_4164699905030176976_n.jpg", label: "🏢 Entrée du studio" },
            { src: "https://customer-assets-agu9un31.emergentagment.net/job_dyvy-musicbox/artifacts/0z2eokq4_825309667_1648184090359400_430710472766063249_n.jpg", label: "🎤 Session live" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/1t281teg_825309749_1663851315109728_8439847471663025426_n.jpg", label: "🎵 En création" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/uoq5n6l1_825309727_1396851568708372_4027477132971379029_n.jpg", label: "👨‍🎤 Ingénieurs" },
            { src: "https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/ai9bndfc_829380266_1643502923787446_1243919767703054682_n.jpg", label: "🎛️ Équipement", span: 2 }
          ].map((photo, idx) => (
            <div key={idx} className={`relative overflow-hidden rounded-xl shadow-lg group cursor-pointer border border-gray-200 ${photo.span === 2 ? 'md:col-span-2' : ''}`}>
              <img 
                src={photo.src}
                alt={photo.label}
                className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                <p className="text-white font-semibold p-4">{photo.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Formulaires */}
      <div className="max-w-4xl mx-auto px-4 py-12 bg-gray-50">
        <Tabs defaultValue="salle" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white border-2 border-gray-300 mb-8 p-1">
            <TabsTrigger 
              value="salle" 
              className="data-[state=active]:bg-[#E50914] data-[state=active]:text-white text-gray-700 font-semibold py-3"
            >
              🎸 Réserver Salle (10$/h)
            </TabsTrigger>
            <TabsTrigger 
              value="mixage" 
              className="data-[state=active]:bg-[#E50914] data-[state=active]:text-white text-gray-700 font-semibold py-3"
            >
              🎚️ Mixer ma chanson (30$)
            </TabsTrigger>
          </TabsList>

          {/* FORMULAIRE 1: Salle */}
          <TabsContent value="salle">
            <Card className="bg-white border-2 border-gray-300 shadow-lg">
              <CardHeader className="bg-gray-50 border-b-2 border-gray-200">
                <CardTitle className="text-2xl flex items-center gap-2 text-gray-900">
                  <Music className="text-[#E50914]" size={28} />
                  Salle de Répétition
                </CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Studio ouvert de 8h00 à 18h00 (Lundi à Samedi)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 bg-white">
                <form onSubmit={handleSalleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="salle_nom" className="text-gray-900 font-bold flex items-center gap-2">
                      <User size={18} className="text-[#E50914]" />
                      Nom complet *
                    </Label>
                    <Input
                      id="salle_nom"
                      type="text"
                      value={salleForm.nom_complet}
                      onChange={(e) => setSalleForm({ ...salleForm, nom_complet: e.target.value })}
                      placeholder="Votre nom complet"
                      className="bg-white border-2 border-gray-300 text-gray-900 mt-2 focus:border-[#E50914]"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="salle_whatsapp" className="text-gray-900 font-bold flex items-center gap-2">
                      <Phone size={18} className="text-[#E50914]" />
                      Numéro WhatsApp *
                    </Label>
                    <Input
                      id="salle_whatsapp"
                      type="tel"
                      value={salleForm.whatsapp}
                      onChange={(e) => setSalleForm({ ...salleForm, whatsapp: e.target.value })}
                      placeholder="+243..."
                      className="bg-white border-2 border-gray-300 text-gray-900 mt-2 focus:border-[#E50914]"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-gray-900 font-bold flex items-center gap-2">
                      <Calendar size={18} className="text-[#E50914]" />
                      Jour de réservation *
                    </Label>
                    <Select value={salleForm.jour} onValueChange={(value) => setSalleForm({ ...salleForm, jour: value })} required>
                      <SelectTrigger className="bg-white border-2 border-gray-300 text-gray-900 mt-2">
                        <SelectValue placeholder="Choisissez un jour" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-2 border-gray-300 text-gray-900">
                        {jours.map((jour) => (
                          <SelectItem key={jour} value={jour}>{jour}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-orange-600 mt-1 font-semibold">⚠️ Le studio est fermé le dimanche</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="heure_debut" className="text-gray-900 font-bold flex items-center gap-2">
                        <Clock size={18} className="text-[#E50914]" />
                        Heure de début *
                      </Label>
                      <Input
                        id="heure_debut"
                        type="time"
                        min="08:00"
                        max="18:00"
                        value={salleForm.heure_debut}
                        onChange={(e) => setSalleForm({ ...salleForm, heure_debut: e.target.value })}
                        className="bg-white border-2 border-gray-300 text-gray-900 mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="heure_fin" className="text-gray-900 font-bold flex items-center gap-2">
                        <Clock size={18} className="text-[#E50914]" />
                        Heure de fin *
                      </Label>
                      <Input
                        id="heure_fin"
                        type="time"
                        min="08:00"
                        max="18:00"
                        value={salleForm.heure_fin}
                        onChange={(e) => setSalleForm({ ...salleForm, heure_fin: e.target.value })}
                        className="bg-white border-2 border-gray-300 text-gray-900 mt-2"
                        required
                      />
                    </div>
                  </div>

                  {montantSalle > 0 && (
                    <div className="bg-orange-50 border-2 border-[#E50914] rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-700 text-sm font-semibold">Durée totale</p>
                          <p className="text-2xl font-bold text-gray-900">{heuresTotal.toFixed(1)} heure{heuresTotal > 1 ? 's' : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-700 text-sm font-semibold">MONTANT TOTAL</p>
                          <p className={`text-4xl font-black ${isAdminAdil ? 'text-green-600' : 'text-[#E50914]'}`}>
                            {isAdminAdil ? 'GRATUIT' : `${montantSalle}$`}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-3 font-medium">
                        Calcul: {heuresTotal.toFixed(1)}h x 10$ = {montantSalle}$
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || montantSalle <= 0}
                    className="w-full bg-gradient-to-r from-[#E50914] to-orange-600 hover:from-[#F8212C] hover:to-orange-700 text-white py-6 text-xl font-bold shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={24} />
                        Traitement...
                      </>
                    ) : (
                      <>💳 PAYER MAINTENANT {!isAdminAdil && `- ${montantSalle}$`}</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FORMULAIRE 2: Mixage */}
          <TabsContent value="mixage">
            <Card className="bg-white border-2 border-gray-300 shadow-lg">
              <CardHeader className="bg-gray-50 border-b-2 border-gray-200">
                <CardTitle className="text-2xl flex items-center gap-2 text-gray-900">
                  <Radio className="text-[#E50914]" size={28} />
                  Mixage Audio Professionnel
                </CardTitle>
                <CardDescription className="text-gray-600 font-medium">
                  Envoyez votre chanson et nos ingénieurs s'en occuperont
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 bg-white">
                <form onSubmit={handleMixageSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="mixage_nom" className="text-gray-900 font-bold flex items-center gap-2">
                      <User size={18} className="text-[#E50914]" />
                      Nom complet *
                    </Label>
                    <Input
                      id="mixage_nom"
                      type="text"
                      value={mixageForm.nom_complet}
                      onChange={(e) => setMixageForm({ ...mixageForm, nom_complet: e.target.value })}
                      placeholder="Votre nom complet"
                      className="bg-white border-2 border-gray-300 text-gray-900 mt-2 focus:border-[#E50914]"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mixage_whatsapp" className="text-gray-900 font-bold flex items-center gap-2">
                      <Phone size={18} className="text-[#E50914]" />
                      Numéro WhatsApp *
                    </Label>
                    <Input
                      id="mixage_whatsapp"
                      type="tel"
                      value={mixageForm.whatsapp}
                      onChange={(e) => setMixageForm({ ...mixageForm, whatsapp: e.target.value })}
                      placeholder="+243..."
                      className="bg-white border-2 border-gray-300 text-gray-900 mt-2 focus:border-[#E50914]"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="titre_chanson" className="text-gray-900 font-bold flex items-center gap-2">
                      <Music size={18} className="text-[#E50914]" />
                      Titre de votre chanson *
                    </Label>
                    <Input
                      id="titre_chanson"
                      type="text"
                      value={mixageForm.titre_chanson}
                      onChange={(e) => setMixageForm({ ...mixageForm, titre_chanson: e.target.value })}
                      placeholder="Titre de la chanson"
                      className="bg-white border-2 border-gray-300 text-gray-900 mt-2 focus:border-[#E50914]"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="fichier_audio" className="text-gray-900 font-bold flex items-center gap-2">
                      <Upload size={18} className="text-[#E50914]" />
                      Fichier audio * (MP3/WAV, max 50MB)
                    </Label>
                    <div className="mt-2">
                      <label
                        htmlFor="fichier_audio"
                        className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-400 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="text-center">
                          <Upload className="mx-auto mb-2 text-gray-500" size={40} />
                          <p className="text-sm text-gray-700 font-semibold">
                            {mixageForm.fichier_audio ? (
                              <span className="text-green-600">✓ {mixageForm.fichier_audio.name}</span>
                            ) : (
                              <>Cliquez pour uploader</>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">MP3, WAV (max 50MB)</p>
                        </div>
                      </label>
                      <input
                        id="fichier_audio"
                        type="file"
                        accept="audio/mp3,audio/wav,audio/mpeg,.mp3,.wav"
                        onChange={handleFileChange}
                        className="hidden"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-gray-900 font-bold">
                      Notes pour l'ingénieur (optionnel)
                    </Label>
                    <Textarea
                      id="notes"
                      value={mixageForm.notes}
                      onChange={(e) => setMixageForm({ ...mixageForm, notes: e.target.value })}
                      placeholder="Ex: Plus de basse, reverb sur la voix..."
                      className="bg-white border-2 border-gray-300 text-gray-900 min-h-[100px] mt-2 focus:border-[#E50914]"
                    />
                  </div>

                  <div className="bg-orange-50 border-2 border-[#E50914] rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-lg text-gray-700 font-semibold">Montant fixe:</span>
                      <span className={`text-5xl font-black ${isAdminAdil ? 'text-green-600' : 'text-[#E50914]'}`}>
                        {isAdminAdil ? 'GRATUIT' : '30$'}
                      </span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !mixageForm.fichier_audio}
                    className="w-full bg-gradient-to-r from-[#E50914] to-orange-600 hover:from-[#F8212C] hover:to-orange-700 text-white py-6 text-xl font-bold shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={24} />
                        Envoi...
                      </>
                    ) : (
                      <>🎵 PAYER {!isAdminAdil && '30$'} ET ENVOYER</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Info Section */}
      <div className="bg-gray-100 py-12 px-4 mt-12 border-t-2 border-gray-300">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8 text-gray-900">📋 Informations importantes</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white border-2 border-gray-300 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">🎸 Salle de Répétition</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700 space-y-2">
                <p>✓ Ouvert de 8h à 18h (Lundi-Samedi)</p>
                <p>✓ Équipement complet fourni</p>
                <p>✓ Tarif: 10$ par heure</p>
                <p>✓ Confirmation WhatsApp après paiement</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-2 border-gray-300 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">🎚️ Mixage Audio</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700 space-y-2">
                <p>✓ Ingénieurs professionnels</p>
                <p>✓ Délai: 2-3 jours ouvrés</p>
                <p>✓ Tarif fixe: 30$</p>
                <p>✓ Résultat envoyé par WhatsApp</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioMusicalPage;
